import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, useGetProfile } from "../hooks";
import { APP_HOME_PATH } from "../lib/app-paths";
import { addDaysIso, todayIso } from "../lib/format";

export const CreateMatchPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading } = useAuth();
  const { data: profile, isLoading: profileLoading } = useGetProfile(user?.id);
  const [location, setLocation] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [spotsNeeded, setSpotsNeeded] = useState(2);
  const [note, setNote] = useState("");
  const [authType, setAuthType] = useState<"guest" | "user">("guest");
  const [locationError, setLocationError] = useState("");
  const [dateError, setDateError] = useState("");
  const [timeError, setTimeError] = useState("");
  const [formError, setFormError] = useState("");

  const minDate = todayIso();
  const maxDate = addDaysIso(14);

  const validate = () => {
    let valid = true;
    if (!location.trim()) {
      setLocationError(t("create.locationRequired"));
      valid = false;
    } else {
      setLocationError("");
    }

    if (!date) {
      setDateError(t("create.dateRequired"));
      valid = false;
    } else if (date < minDate || date > maxDate) {
      setDateError(t("create.dateRange"));
      valid = false;
    } else {
      setDateError("");
    }

    if (!time) {
      setTimeError(t("create.timeRequired"));
      valid = false;
    } else {
      setTimeError("");
    }

    return valid;
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!validate()) return;

    const draft = {
      location: location.trim(),
      date,
      time,
      spotsNeeded,
      note: note.trim() || undefined,
    };

    if (isAuthenticated && user) {
      if (profileLoading || !profile) {
        setFormError(t("create.profileError"));
        return;
      }
      navigate("/crear/jugadores", {
        state: {
          draft: {
            ...draft,
            userId: user.id,
            userEmail: user.email,
            userName: profile.name,
          },
        },
      });
      return;
    }

    if (authType === "guest") {
      navigate("/crear/jugadores", { state: { draft } });
      return;
    }

    navigate("/login", { state: { draft } });
  };

  return (
    <main className="pp-screen">
      <header className="pp-header">
        <Link to={APP_HOME_PATH} className="pp-back">
          {t("common.backHome")}
        </Link>
        <h1>{t("create.title")}</h1>
        <p>{t("create.subtitle")}</p>
      </header>

      <form className="pp-card" onSubmit={handleSubmit}>
        <div className="pp-field">
          <label htmlFor="location">{t("create.where")}</label>
          <input
            id="location"
            placeholder={t("create.wherePlaceholder")}
            value={location}
            onChange={(event) => setLocation(event.target.value)}
          />
          {locationError && <p className="pp-error">{locationError}</p>}
        </div>

        <div className="pp-field">
          <label htmlFor="date">{t("create.when")}</label>
          <div className="pp-chips">
            {[
              { label: t("create.today"), days: 0 },
              { label: t("create.tomorrow"), days: 1 },
              { label: t("create.dayAfter"), days: 2 },
            ].map((option) => {
              const value = addDaysIso(option.days);
              return (
                <button
                  key={option.label}
                  type="button"
                  className={`pp-chip ${date === value ? "is-selected" : ""}`}
                  onClick={() => setDate(value)}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
          <input
            id="date"
            type="date"
            min={minDate}
            max={maxDate}
            value={date}
            onChange={(event) => setDate(event.target.value)}
          />
          {dateError && <p className="pp-error">{dateError}</p>}
        </div>

        <div className="pp-field">
          <label htmlFor="time">{t("create.time")}</label>
          <input
            id="time"
            type="time"
            value={time}
            onChange={(event) => setTime(event.target.value)}
          />
          {timeError && <p className="pp-error">{timeError}</p>}
        </div>

        <div className="pp-field">
          <span className="pp-legend">{t("create.spots")}</span>
          <div className="pp-chips">
            {[1, 2, 3].map((num) => (
              <button
                key={num}
                type="button"
                className={`pp-chip ${
                  spotsNeeded === num ? "is-selected" : ""
                }`}
                onClick={() => setSpotsNeeded(num)}
              >
                {num}
              </button>
            ))}
          </div>
        </div>

        <div className="pp-field">
          <label htmlFor="note">{t("create.note")}</label>
          <textarea
            id="note"
            placeholder={t("create.notePlaceholder")}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </div>

        {!loading && !isAuthenticated && (
          <fieldset className="pp-choice">
            <legend className="pp-legend">{t("create.how")}</legend>
            <label className={authType === "guest" ? "is-selected" : ""}>
              <input
                type="radio"
                name="authType"
                value="guest"
                checked={authType === "guest"}
                onChange={() => setAuthType("guest")}
              />
              👤 {t("create.guest")}
              <span>{t("create.guestHint")}</span>
            </label>
            <label className={authType === "user" ? "is-selected" : ""}>
              <input
                type="radio"
                name="authType"
                value="user"
                checked={authType === "user"}
                onChange={() => setAuthType("user")}
              />
              ⭐ {t("create.user")}
              <span>{t("create.userHint")}</span>
            </label>
          </fieldset>
        )}

        {formError && <p className="pp-error">{formError}</p>}

        <button type="submit" className="pp-btn" disabled={loading}>
          {isAuthenticated ? t("create.title") : t("common.continue")}
        </button>
      </form>
    </main>
  );
};
